import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import type { AuthUser, LoginCredentials, LoginResponse, BrandId } from "@/types"
import { post } from "@/services/api"
import { API_ENDPOINTS } from "@/config/api"
import { mensajeDeError } from "@/lib/mensaje-de-error"
import api from "@/services/api"
import { getBrandFromHostname } from "@/config/brands"

interface AuthState {
  user: AuthUser | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // Actions
  login: (credentials: LoginCredentials) => Promise<boolean>
  logout: () => void
  refreshUser: () => Promise<void>
  setTokens: (token: string, refreshToken: string) => void
  clearError: () => void
  // Marca que el usuario ya cambió su password temporal (limpia el gate forzado).
  markPasswordChanged: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await post<LoginResponse>(
            API_ENDPOINTS.AUTH.LOGIN,
            credentials
          )
          
          if (response.success && response.tokens) {
            // Mapear campos del sistema original
            const userData = (response as any).data || response.user

            // Defensa multitenant: la marca del usuario debe coincidir con
            // el hostname. Usar `brand_slug` (FK → brands.slug) — el campo
            // `empresa` es texto libre que el usuario puede llenar con
            // cualquier cosa al crear su cuenta (ej. "Pandanet" en marca
            // pandamovil) y NO sirve para validar marca.
            const expectedBrand: BrandId = getBrandFromHostname()
            const userBrand = (userData.brand_slug || "").toLowerCase()
            if (userBrand && userBrand !== expectedBrand && !userData.is_super) {
              set({
                error: `Usuario pertenece a otra marca (${userBrand}); ingrese desde el dominio correcto.`,
                isLoading: false,
              })
              return false
            }

            const mappedUser: AuthUser = {
              id_usuario: userData.id_usuario,
              email: userData.email_usuario || userData.email,
              usuario: userData.email_usuario || userData.email,
              nombre: userData.nombre_usuario || userData.nombre,
              apellidos: userData.apellidos || "",
              empresa: userData.empresa || "panda",
              numero_telefonico: userData.numero_telefonico,
              tipo_usuario: userData.tipo_usuario as any,
              padre: userData.padre,
              activo: true,
              img_perfil: userData.img_profile || userData.img_perfil,
              dado_alta: userData.desde || new Date().toISOString(),
              actualizado: new Date().toISOString(),
              cuenta_bancaria: userData.cuenta_bancaria,
              banco: userData.banco,
              referencia: userData.referencia,
              pilas: userData.pilas as string | undefined,
              wallet1: userData.wallet1,
              wallet2: userData.wallet2,
              wallet4: userData.wallet4,
              wallet5: userData.wallet5,
              credito_ilimitado: userData.credito_ilimitado ?? false,
              permisos: userData.permisos,
              es_subcuenta: userData.es_subcuenta ?? false,
              subcuenta_padre_id: userData.subcuenta_padre_id ?? null,
              // El tipo `User` ya declaraba este campo pero nadie lo poblaba:
              // quedaba en undefined (falsy) siempre. Cualquier pantalla que
              // lo usara para mostrar u ocultar la venta de eSIM habria
              // reproducido el mismo bug que acabamos de cerrar en el detalle
              // del usuario.
              esim_activacion_habilitada: userData.esim_activacion_habilitada ?? false,
              venta_recargas_habilitada: userData.venta_recargas_habilitada ?? false,
              pago_servicios_habilitado: userData.pago_servicios_habilitado ?? false,
              debe_cambiar_password: userData.debe_cambiar_password ?? false,
            }

            set({
              user: mappedUser,
              token: response.tokens.access_token,
              refreshToken: response.tokens.refresh_token,
              isAuthenticated: true,
              isLoading: false,
            })
            return true
          } else {
            const data = (response as any).data
            const msg = data?.valido === false ? (data.msg as string) : null
            set({
              error: msg || "Credenciales inválidas",
              isLoading: false,
            })
            return false
          }
        } catch (error: unknown) {
          set({
            error: mensajeDeError(error, "iniciar sesión", { esLogin: true }),
            isLoading: false,
          })
          return false
        }
      },
      
      logout: () => {
        // Avisar al backend: es lo único que borra la cookie httpOnly de
        // `/uploads` (JS no puede tocarla). Sin esto, "cerrar sesión" dejaba el
        // acceso a comprobantes y cédulas vivo en el navegador — crítico en
        // terminales compartidas.
        //
        // El token va EXPLÍCITO en el config: el interceptor de request lo lee
        // del store en un microtask, o sea DESPUÉS del `set()` de abajo, así
        // que la petición saldría sin credencial y el backend nunca borraría
        // la cookie. Best-effort: si falla la red, igual cerramos localmente.
        const token = get().token
        if (token) {
          void api
            .post(API_ENDPOINTS.AUTH.LOGOUT, null, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => undefined)
        }

        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        })
      },
      
      refreshUser: async () => {
        const state = get()
        if (!state.token) return
        
        try {
          const response = await api.get<{ success: boolean; data?: Record<string, unknown> }>(
            API_ENDPOINTS.AUTH.ME
          )
          const body = response.data
          if (body?.success && body?.data) {
            const userData = body.data as Record<string, unknown>
            const mappedUser: AuthUser = {
              id_usuario: userData.id_usuario as number,
              email: (userData.email_usuario ?? userData.email) as string,
              usuario: (userData.email_usuario ?? userData.email) as string,
              nombre: (userData.nombre_usuario ?? userData.nombre) as string,
              apellidos: (userData.apellidos ?? "") as string,
              empresa: (userData.empresa ?? "panda") as string,
              numero_telefonico: userData.numero_telefonico as string | undefined,
              tipo_usuario: userData.tipo_usuario as AuthUser["tipo_usuario"],
              padre: userData.padre as string | undefined,
              activo: true,
              img_perfil: (userData.img_profile ?? userData.img_perfil) as string | undefined,
              dado_alta: (userData.desde ?? new Date().toISOString()) as string,
              actualizado: new Date().toISOString(),
              cuenta_bancaria: userData.cuenta_bancaria as string | undefined,
              banco: userData.banco as string | undefined,
              referencia: userData.referencia as string | undefined,
              pilas: userData.pilas as string | undefined,
              wallet1: userData.wallet1 as number | undefined,
              wallet2: userData.wallet2 as number | undefined,
              wallet4: userData.wallet4 as number | undefined,
              wallet5: userData.wallet5 as number | undefined,
              credito_ilimitado: (userData.credito_ilimitado as boolean) ?? false,
              permisos: userData.permisos as Record<string, string> | undefined,
              es_subcuenta: (userData.es_subcuenta as boolean) ?? false,
              subcuenta_padre_id: (userData.subcuenta_padre_id as number | null) ?? null,
              debe_cambiar_password: (userData.debe_cambiar_password as boolean) ?? false,
              // El login sí lo mapeaba, pero `refreshUser` no: al refrescar la sesión el
              // permiso volvía a `undefined` (falsy) y se perdía. `/auth/me` lo manda —
              // comparte `format_user_data` con el login (auth_service.py:459).
              esim_activacion_habilitada: (userData.esim_activacion_habilitada as boolean) ?? false,
              // Recargas y servicios: mismo cuidado que eSIM — si refreshUser no los mapea, el
              // menú desaparece al refrescar la sesión aunque el distribuidor los haya concedido.
              venta_recargas_habilitada: (userData.venta_recargas_habilitada as boolean) ?? false,
              pago_servicios_habilitado: (userData.pago_servicios_habilitado as boolean) ?? false,
            }
            set({ user: mappedUser })
          }
        } catch {
          // Token invalid - logout
          state.logout()
        }
      },
      
      setTokens: (token: string, refreshToken: string) => {
        set({ token, refreshToken })
      },
      
      clearError: () => {
        set({ error: null })
      },

      markPasswordChanged: () => {
        const u = get().user
        if (u) set({ user: { ...u, debe_cambiar_password: false } })
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
